import React, { useRef } from "react";
import Draft from "draft-js";
import { pick } from "lodash";
import { useSearchTerm } from "@sonder/editor/source/core/components/SearchTerm";
import {
  ENABLE_INCOMPLETE_FEATURES,
  INLINE_STYLES,
  InlineStyles,
} from "@sonder/editor/source/core/constants";
import {
  commandReducer,
  mapKeyToCommand,
  useEditorStore,
} from "@sonder/editor/source/core/stores";

const DEFERRED_COMMANDS = [
  "backspace",
  "delete",
] as (Draft.DraftEditorCommand | null)[];

interface DraftEditorProps {
  disabled?: boolean;
  placeholder?: string;
  readOnly: boolean;
}
export const DraftEditor = ({
  disabled,
  placeholder,
  readOnly,
}: DraftEditorProps) => {
  const { state, setState } = useEditorStore();
  const { searchTermEntityKey, cancelSearchTerm, onKeyboardInput } =
    useSearchTerm();

  const isSearchTermAction = Boolean(searchTermEntityKey);

  const editorRef = useRef<Draft.Editor>(null);

  return (
    <Draft.Editor
      customStyleMap={
        ENABLE_INCOMPLETE_FEATURES
          ? INLINE_STYLES
          : pick(INLINE_STYLES, InlineStyles.Link)
      }
      editorState={state}
      handleKeyCommand={command => {
        const nextState = commandReducer(state, command, isSearchTermAction);
        if (nextState) {
          setState(nextState);
          return "handled";
        }
        return "not-handled";
      }}
      handlePastedText={(text, html, editorState) => {
        const content = editorState.getCurrentContent();
        const selection = editorState.getSelection();

        // Only allow pasting plain text
        setState(
          Draft.EditorState.push(
            editorState,
            Draft.Modifier.replaceText(content, selection, text),
            "insert-characters",
          ),
        );

        // Cancel search term when users paste in any content
        if (isSearchTermAction) cancelSearchTerm();

        return "handled";
      }}
      keyBindingFn={(e: React.KeyboardEvent) => {
        const command = mapKeyToCommand(e);

        // Handle search term-related commands separately
        // !TODO: Instead of deferring key commands, map them to named editor
        // commands and handle state changes in `handleKeyCommand`
        if (!command || DEFERRED_COMMANDS.includes(command)) onKeyboardInput(e);

        return command;
      }}
      placeholder={placeholder}
      readOnly={readOnly || disabled}
      ref={editorRef}
      onChange={setState}
    />
  );
};
